import { ScreenLayout } from "@/blocks/ScreenLayout";
import { SupportCallButton } from "@/blocks/SupportCallButton";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard, VnCardTitle } from "@/components/VnCard";
import { VnText } from "@/components/VnText/VnText";
import { useGetSignedConsentDocumentsQuery } from "@/data/queries/consentDocumentsQueries";
import { SUPPORT_EMAIL } from "@/domain/constants";
import { useCurrentPatient } from "@/hooks/useCurrentPatient";
import { useDownloadSignedConsentDocuments } from "@/hooks/useDownloadSignedConsentDocuments.web";
import { useTranslator } from "@/hooks/useTranslate";
import { useAuthEmail, useLoginMethod } from "@/stores/authStore";

import { DashboardLayout } from "./DashboardLayout";

export const AccountScreen = () => {
  const { t } = useTranslator();
  const authEmail = useAuthEmail();
  const { username } = useCurrentPatient();
  // The alias address a username account signs in with must never be shown.
  const accountIdentifier = username ?? authEmail;
  const authProviderMethod = useLoginMethod();
  const loginMethod = username
    ? t("native:accountScreen.usernameLoginMethod")
    : authProviderMethod;
  const { refetch, isFetching } = useGetSignedConsentDocumentsQuery({
    isEnabled: false,
  });
  const { downloadSignedConsentDocuments, isDownloading } =
    useDownloadSignedConsentDocuments();

  const handleDownloadSignedDocument = async () => {
    const { data } = await refetch();

    if (!data || data.length === 0) return;

    await downloadSignedConsentDocuments(data);
  };

  return (
    <DashboardLayout shouldShowFooter={false}>
      <ScreenLayout>
        <VnBox className="mt-safe-offset-10 web:py-4 w-full gap-8 md:max-w-screen-md md:self-center">
          <VnBox className="gap-5">
            <VnText variant="headerTwo" i18nKey="native:accountScreen.title" />
            <VnCard>
              <VnCardTitle
                i18nKey="native:consentDocuments.title"
                variant="headerTwo"
              />
              <VnBox className="gap-3">
                <VnText
                  i18nKey="native:consentDocuments.grantSubtitle"
                  variant="lgBold"
                />
                <ul className="ml-2 list-inside list-disc">
                  <li>
                    <VnText
                      i18nKey="native:consentDocuments.grant1"
                      variant="lg"
                    />
                  </li>
                  <li>
                    <VnText
                      i18nKey="native:consentDocuments.grant2"
                      variant="lg"
                    />
                  </li>
                  <li>
                    <VnText
                      i18nKey="native:consentDocuments.grant3"
                      variant="lg"
                    />
                  </li>
                  <li>
                    <VnText
                      i18nKey="native:consentDocuments.grantHealthPlanSelection"
                      variant="lg"
                    />
                  </li>
                  <li>
                    <VnText
                      i18nKey="native:consentDocuments.grant4"
                      variant="lg"
                    />
                  </li>
                </ul>
              </VnBox>
              <VnButton
                variant="outline"
                i18nKey="native:accountScreen.downloadCta"
                isLoading={isFetching || isDownloading}
                onPress={() => {
                  void handleDownloadSignedDocument();
                }}
              />
            </VnCard>
            <VnCard>
              <VnCardTitle
                i18nKey="native:accountScreen.helpTitle"
                variant="headerTwo"
              />
              <VnText
                i18nKey="native:accountScreen.helpDescription"
                i18nOptions={{ email: SUPPORT_EMAIL }}
                variant="lg"
              />
              <SupportCallButton
                className="web:h-auto web:items-start web:justify-start h-14 rounded-full"
                textClassName="web:font-primary web:text-base web:leading-6"
              />
            </VnCard>
            <VnBox className="items-center gap-1">
              <VnText variant="lg">{accountIdentifier}</VnText>
              <VnText
                i18nKey="native:accountScreen.loggedInWith"
                i18nOptions={{ method: loginMethod }}
                variant="lg"
                className="text-muted-foreground"
              />
            </VnBox>
          </VnBox>
        </VnBox>
      </ScreenLayout>
    </DashboardLayout>
  );
};
